(() => {
  if (window.O2OShell) return;
  const mobile = window.matchMedia('(max-width: 900px)');
  const visibleFocus = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const welcomeStorage = 'o2o-welcome-seen';
  const searchesStorage = 'o2o-recent-searches';
  const newsletterStorage = 'o2o-newsletter-pending';
  let activeSurface = null;
  let previousFocus = null;
  let searchController = null;
  let searchTimer = null;
  let searchRevision = 0;
  let welcomeTimer = null;
  let welcomeScrollHandler = null;
  let welcomeOpened = false;
  let originalSearch = null;
  let revealObserver = null;
  let videoObserver = null;
  let historyCloseTimer = null;
  let historyBackPending = false;
  let historyQueuedSurface = null;
  let newsletterBusy = false;
  const historyOwner = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const timers = new WeakMap();
  const surfaceAnchors = new WeakMap();
  const surfaceParents = new WeakMap();
  const get = id => document.getElementById(id);
  const injectedDebugBorder = /^(?:[1-9]|1[0-2])px\s+solid\s+(?:red|#f00(?:000)?|rgb\(\s*255\s*,\s*0\s*,\s*0\s*\))$/i;
  const clearInjectedDebugBorder = element => {
    if (!(element instanceof HTMLElement)) return;
    const border = element.style.getPropertyValue('border').replace(/\s+/g, ' ').trim();
    if (element.style.getPropertyPriority('border') === 'important' && injectedDebugBorder.test(border)) {
      element.style.removeProperty('border');
    }
  };
  const setupDebugBorderGuard = () => {
    document.querySelectorAll('[style]').forEach(clearInjectedDebugBorder);
    const observer = new MutationObserver(records => records.forEach(record => clearInjectedDebugBorder(record.target)));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'], subtree: true });
  };
  const storageRead = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch (_) { return fallback; }
  };
  const storageWrite = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };
  const strings = () => {
    try { return JSON.parse(document.querySelector('[data-o2o-shell-text]')?.textContent || '{}'); } catch (_) { return {}; }
  };

  function syncHeader() {
    const solid = window.scrollY > 44 || Boolean(activeSurface) || Boolean(document.querySelector('.has-mega.is-open'));
    get('siteMasthead')?.classList.toggle('is-solid', solid);
    get('siteHeader')?.classList.toggle('is-solid', solid);
  }

  function closeMegaMenus(except) {
    document.querySelectorAll('.has-mega').forEach(item => {
      if (item === except) return;
      clearTimeout(timers.get(item));
      item.classList.remove('is-open');
      item.querySelectorAll('[aria-expanded]').forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
    });
    syncHeader();
  }

  function openMega(item) {
    if (activeSurface?.id === 'searchPanel') return;
    clearTimeout(timers.get(item));
    closeMegaMenus(item);
    item.classList.add('is-open');
    item.querySelectorAll(':scope > [aria-expanded]').forEach(trigger => trigger.setAttribute('aria-expanded', 'true'));
    syncHeader();
  }

  function syncSurfaces() {
    const modal = activeSurface && activeSurface.id !== 'accountPopover';
    const backdrop = modal && activeSurface.id !== 'welcomeModal' && activeSurface.id !== 'searchPanel';
    get('siteBackdrop')?.classList.toggle('is-open', Boolean(backdrop));
    document.body.classList.toggle('is-locked', Boolean(modal));
    document.querySelectorAll('[aria-controls]').forEach(control => {
      if (control.matches('[data-o2o-surface-open],[data-commerce-open],[data-cart-open]')) control.setAttribute('aria-expanded', String(control.getAttribute('aria-controls') === activeSurface?.id));
    });
    const navKey = { cartDrawer: 'bag', searchPanel: 'search' }[activeSurface?.id];
    document.querySelectorAll('[data-mobile-nav]').forEach(control => {
      const initial = control.dataset.mobileBaseActive === 'true';
      control.classList.toggle('is-active', navKey ? control.dataset.mobileNav === navKey : initial);
    });
    get('siteHeader')?.classList.toggle('is-search-open', activeSurface?.id === 'searchPanel');
    syncHeader();
  }

  function ownedHistorySurface() {
    const entry = history.state?.__o2oSurface;
    return entry?.owner === historyOwner ? entry : null;
  }

  function markSurfaceHistory(id) {
    if (!mobile.matches || id === 'accountPopover' || id === 'welcomeModal') return;
    clearTimeout(historyCloseTimer);
    const entry = { owner: historyOwner, id };
    const existing = history.state && typeof history.state === 'object' ? history.state : {};
    try {
      if (ownedHistorySurface()) history.replaceState({ ...existing, __o2oSurface: entry }, '', location.href);
      else history.pushState({ ...existing, __o2oSurface: entry }, '', location.href);
    } catch (_) {}
  }

  function dismissSurfaceHistory(id) {
    clearTimeout(historyCloseTimer);
    if (historyQueuedSurface?.id === id) historyQueuedSurface = null;
    if (historyBackPending || ownedHistorySurface()?.id !== id) return;
    historyCloseTimer = setTimeout(() => {
      if (activeSurface || !ownedHistorySurface()) return;
      historyBackPending = true;
      history.back();
    }, 0);
  }

  function portalSurface(surface) {
    // Product sections sit in a lower stacking context than the shared backdrop.
    // Keep the original position so section-level behavior resumes after closing.
    const nestedSurface=surface.parentElement?.closest('.modal,.drawer,.collection-curtain');
    if ((!surface.closest('main') && !nestedSurface) || surfaceAnchors.has(surface)) return;
    const anchor = document.createComment(`o2o-surface:${surface.id}`);
    surface.before(anchor);
    surfaceAnchors.set(surface, anchor);
    document.body.append(surface);
  }

  function restoreSurface(surface) {
    const anchor = surfaceAnchors.get(surface);
    if (!anchor) return;
    if (anchor.isConnected) anchor.replaceWith(surface);
    else surface.remove();
    surfaceAnchors.delete(surface);
  }

  function closeSurface(id, options = {}) {
    const surface = typeof id === 'string' ? get(id) : id || activeSurface;
    if (!surface) return;
    const wasOpen = surface.classList.contains('is-open');
    surface.classList.remove('is-open');
    surface.setAttribute('aria-hidden', 'true');
    surface.inert = true;
    if (activeSurface === surface) activeSurface = null;
    if (surface.id === 'welcomeModal') storageWrite(welcomeStorage, Date.now());
    if (wasOpen && options.history !== false) dismissSurfaceHistory(surface.id);
    restoreSurface(surface);
    syncSurfaces();
    if (wasOpen) document.dispatchEvent(new CustomEvent('o2o:surface-close', { detail: { id: surface.id } }));
    const parent = surfaceParents.get(surface);
    surfaceParents.delete(surface);
    if (wasOpen && options.restoreFocus !== false && parent?.surface?.isConnected) {
      openSurface(parent.surface, {focus: parent.focus, history: options.history});
      return;
    }
    if (options.restoreFocus !== false && previousFocus?.isConnected && !activeSurface) previousFocus.focus({ preventScroll: true });
  }

  function closeAll(options = {}) {
    closeSurface(activeSurface, options);
    closeMegaMenus();
  }

  function openSurface(id, options = {}) {
    const surface = typeof id === 'string' ? get(id) : id;
    if (!surface) return false;
    if (activeSurface === surface) return true;
    clearTimeout(historyCloseTimer);
    const nestedParent = surface.parentElement?.closest('.modal,.drawer,.collection-curtain');
    if (nestedParent && nestedParent === activeSurface) surfaceParents.set(surface,{surface:nestedParent,focus:document.activeElement});
    const focused = activeSurface?.contains(document.activeElement) ? previousFocus : document.activeElement;
    closeAll({ restoreFocus: false, history: false });
    previousFocus = focused;
    activeSurface = surface;
    portalSurface(surface);
    surface.inert = false;
    surface.classList.add('is-open');
    surface.setAttribute('aria-hidden', 'false');
    if (options.history !== false) {
      if (historyBackPending) historyQueuedSurface = { id: surface.id };
      else markSurfaceHistory(surface.id);
    }
    syncSurfaces();
    if (surface.id === 'searchPanel') {
      rememberInitialSearch();
      renderRecentSearches();
    }
    if (surface.id === 'welcomeModal') {
      welcomeOpened = true;
      clearTimeout(welcomeTimer);
      storageWrite(welcomeStorage, Date.now());
    }
    document.dispatchEvent(new CustomEvent('o2o:surface-open', { detail: { id: surface.id } }));
    const focusSurface = () => {
      if (activeSurface !== surface) return;
      const welcomeFocus = surface.querySelector('[data-welcome-success]:not([hidden]), #WelcomeFormError:not([hidden])') || (mobile.matches ? surface.querySelector('.welcome-close') : get('welcomeEmail'));
      const target = options.focus || (surface.id === 'searchPanel' ? get('searchInput') : surface.id === 'welcomeModal' ? welcomeFocus : surface.querySelector('[autofocus], [data-o2o-surface-close], button, input, a[href], [tabindex]'));
      if (target?.focus) target.focus({ preventScroll: true });
      else { surface.tabIndex = -1; surface.focus({ preventScroll: true }); }
    };
    requestAnimationFrame(() => {
      focusSurface();
      setTimeout(() => {
        if (activeSurface === surface && !surface.contains(document.activeElement)) focusSurface();
      }, 120);
    });
    return true;
  }

  function trapFocus(event) {
    if (!activeSurface || activeSurface.id === 'accountPopover' || event.key !== 'Tab') return;
    const nodes = Array.from(activeSurface.querySelectorAll(visibleFocus)).filter(node => node.getClientRects().length && !node.closest('[hidden],[inert]'));
    if (!nodes.length) { event.preventDefault(); return; }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!activeSurface.contains(document.activeElement)) { event.preventDefault(); first.focus(); }
    else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function rememberInitialSearch() {
    if (originalSearch) return;
    originalSearch = {
      products: get('searchProducts')?.innerHTML || ''
    };
  }

  function renderRecentSearches() {
    const wrapper = get('mobileRecentSearches');
    const target = get('recentSearchList');
    if (!wrapper || !target) return;
    const items = storageRead(searchesStorage, []).filter(term => typeof term === 'string').slice(0, 6);
    wrapper.hidden = !mobile.matches || !items.length || Boolean(get('searchInput')?.value.trim());
    target.replaceChildren(...items.map(term => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.recentSearch = term;
      button.textContent = term;
      return button;
    }));
  }

  function rememberSearch(term) {
    const clean = term.trim().slice(0, 120);
    if (!clean) return;
    const items = storageRead(searchesStorage, []).filter(value => typeof value === 'string' && value.toLowerCase() !== clean.toLowerCase());
    storageWrite(searchesStorage, [clean, ...items].slice(0, 6));
  }

  function searchResultLink(item, isProduct) {
    const link = document.createElement('a');
    let destination;
    try { destination = new URL(item.url, location.origin); } catch (_) { return null; }
    if (destination.origin !== location.origin) return null;
    link.href = destination.pathname + destination.search;
    if (!isProduct) { link.textContent = item.title; return link; }
    link.className = 'search-product-result';
    const src = item.featured_image?.url || item.image;
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.width = 48;
      img.height = 64;
      img.loading = 'lazy';
      link.append(img);
    }
    const text = document.createElement('span');
    text.className = 'search-product-copy';
    const title = document.createElement('strong');
    title.textContent = item.title;
    text.append(title);
    if (item.product_type) {
      const type = document.createElement('small');
      type.textContent = item.product_type;
      text.append(type);
    }
    link.append(text);
    if (item.price != null) {
      const price = document.createElement('span');
      price.className = 'search-result-price money';
      const amount = Number(item.price);
      const currency = window.O2O?.currency || window.Shopify?.currency?.active;
      if (Number.isFinite(amount) && currency) {
        try { price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount); } catch (_) { price.textContent = item.price; }
      } else price.textContent = item.price;
      link.append(price);
    }
    return link;
  }

  async function searchProducts(query) {
    rememberInitialSearch();
    searchController?.abort();
    const revision = ++searchRevision;
    const productTarget = get('searchProducts');
    const text = strings();
    if (!productTarget) return;
    const all = get('searchViewAll');
    if (get('searchQuery')) get('searchQuery').textContent = query || text.hint;
    if (get('searchProductsTitle')) get('searchProductsTitle').textContent = query ? text.results : text.trending;
    if (all) {
      all.hidden = !query;
      all.href = `${document.querySelector('[data-o2o-shell]')?.dataset.searchUrl || '/search'}?q=${encodeURIComponent(query)}&type=product`;
    }
    renderRecentSearches();
    if (!query) {
      productTarget.innerHTML = originalSearch.products;
      productTarget.removeAttribute('aria-busy');
      return;
    }
    productTarget.setAttribute('aria-busy', 'true');
    productTarget.textContent = text.loading;
    searchController = new AbortController();
    try {
      const path = document.querySelector('[data-o2o-shell]')?.dataset.predictiveUrl || '/search/suggest';
      const url = new URL(`${path}.json`, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '4');
      const response = await fetch(url, { signal: searchController.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search unavailable');
      const data = await response.json();
      if (revision !== searchRevision) return;
      const products = data.resources?.results?.products || [];
      productTarget.replaceChildren(...products.map(item => searchResultLink(item, true)).filter(Boolean));
      if (!products.length) productTarget.textContent = text.empty;
    } catch (error) {
      if (error.name === 'AbortError' || revision !== searchRevision) return;
      productTarget.textContent = text.error;
    } finally {
      if (revision === searchRevision) productTarget.removeAttribute('aria-busy');
    }
  }

  function newsletterPending() {
    try {
      const pending = JSON.parse(sessionStorage.getItem(newsletterStorage));
      return pending?.formId && Date.now() - pending.at < 1800000 ? pending : null;
    } catch (_) { return null; }
  }

  function newsletterConfig(form) {
    try { return JSON.parse(form.querySelector('[data-newsletter-config]')?.textContent || '{}'); }
    catch (_) { return {}; }
  }

  function newsletterFeedback(form, status) {
    const feedback = form.querySelector('[data-newsletter-feedback]');
    if (!feedback) return;
    const config = newsletterConfig(form);
    feedback.textContent = config[status] || config.unavailable;
    feedback.hidden = false;
    feedback.setAttribute('role', status === 'unavailable' ? 'alert' : 'status');
    feedback.focus({ preventScroll: false });
  }

  async function newsletterStatus(form, email, mode, receipt = '') {
    const endpoint = new URL(newsletterConfig(form).endpoint, location.origin);
    if (endpoint.protocol !== 'https:') throw new Error('Invalid newsletter endpoint');
    const response = await fetch(endpoint.href, {
      method: 'POST', credentials: 'omit', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email, mode, receipt }), signal: AbortSignal.timeout(15000)
    });
    if (!response.ok) throw new Error('Newsletter status unavailable');
    const result = await response.json();
    if (!['available', 'subscribed', 'pending', 'in_progress', 'not_confirmed'].includes(result.status)) throw new Error('Invalid newsletter status');
    return result;
  }

  function rememberNewsletterSubmission(form) {
    const email = form.querySelector('[type="email"]').value.trim().toLowerCase();
    try { sessionStorage.setItem(newsletterStorage, JSON.stringify({ formId: form.id, email, receipt: form.dataset.newsletterReceipt || '', at: Date.now() })); } catch (_) {}
    const destination = new URL(location.href);
    destination.hash = form.id;
    destination.searchParams.set('customer_posted', 'true');
    let returnTo = form.querySelector('input[name="return_to"]');
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.append(returnTo);
    }
    returnTo.value = `${destination.pathname}${destination.search}${destination.hash}`;
  }

  async function guardNewsletterSubmit(event) {
    const form = event.target;
    const config = newsletterConfig(form);
    const email = form.querySelector('[type="email"]').value.trim().toLowerCase();
    const approved = form.dataset.newsletterPass === 'true' && form.dataset.newsletterApprovedEmail === email;
    if (approved || !config.endpoint) {
      delete form.dataset.newsletterPass;
      delete form.dataset.newsletterApprovedEmail;
      rememberNewsletterSubmission(form);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (newsletterBusy || form.dataset.newsletterConfirmed === 'true') return;
    newsletterBusy = true;
    form.setAttribute('aria-busy', 'true');
    const submitter = event.submitter || form.querySelector('button[type="submit"]');
    const buttons = [...document.querySelectorAll('[data-newsletter-form] button[type="submit"]')];
    const disabled = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; });
    const pending = newsletterPending();
    let accepted = false;
    try {
      const result = await newsletterStatus(form, email, 'prepare', pending?.email === email ? pending.receipt : '');
      if (result.status === 'available' && result.receipt) {
        form.dataset.newsletterReceipt = result.receipt;
        form.dataset.newsletterPass = 'true';
        form.dataset.newsletterApprovedEmail = email;
        accepted = form.querySelector('[type="email"]').value.trim().toLowerCase() === email;
        if (!accepted) {
          delete form.dataset.newsletterPass;
          delete form.dataset.newsletterApprovedEmail;
          newsletterFeedback(form, 'unavailable');
        }
      } else newsletterFeedback(form, result.status);
    } catch (_) { newsletterFeedback(form, 'unavailable'); }
    finally {
      newsletterBusy = false;
      form.removeAttribute('aria-busy');
      buttons.forEach((button, index) => { button.disabled = disabled[index]; });
    }
    if (accepted) form.requestSubmit(submitter);
  }

  function setupNewsletterForms() {
    const pending = newsletterPending();
    let returnedId = '';
    try { returnedId = decodeURIComponent(location.hash.slice(1)); } catch (_) {}
    const targetId = pending?.formId || returnedId;
    document.querySelectorAll('[data-newsletter-form]').forEach(form => {
      const success = form.querySelector('[data-newsletter-success]');
      const error = form.querySelector('[data-newsletter-error]');
      const isTarget = targetId ? targetId === form.id : form.id !== 'welcomeForm';
      if (success) success.hidden = !isTarget;
      if (error) error.hidden = !isTarget;
      if (!isTarget) return;
      if (!success && !error) {
        if (pending?.email && targetId === returnedId && newsletterConfig(form).endpoint && form.dataset.newsletterReconciling !== 'true') {
          form.dataset.newsletterReconciling = 'true';
          newsletterStatus(form, pending.email, 'confirm', pending.receipt).then(result => {
            if (result.status === 'subscribed' || result.status === 'pending') {
              const message = document.createElement('p');
              message.className = 'newsletter-success';
              message.dataset.newsletterSuccess = '';
              message.setAttribute('role', 'status');
              message.textContent = newsletterConfig(form)[result.status === 'pending' ? 'pending' : 'success'];
              form.append(message);
              setupNewsletterForms();
              if (form.id === 'welcomeForm') setupWelcome();
            } else newsletterFeedback(form, 'not_confirmed');
          }).catch(() => newsletterFeedback(form, 'unavailable'));
        }
        return;
      }
      if (error) {
        form.dataset.newsletterError = 'true';
        error.tabIndex = -1;
        if (form.id !== 'welcomeForm') welcomeOpened = true;
        return;
      }
      if (pending?.formId === form.id) {
        try { sessionStorage.removeItem(newsletterStorage); } catch (_) {}
      }
      form.dataset.newsletterConfirmed = 'true';
      storageWrite(welcomeStorage, Date.now());
      form.querySelector('[data-newsletter-fields]')?.setAttribute('hidden', '');
      success.tabIndex = -1;
      if (form.dataset.newsletterAnnounced !== 'true') {
        form.dataset.newsletterAnnounced = 'true';
        document.dispatchEvent(new CustomEvent('o2o:newsletter-success', { detail: { formId: form.id } }));
        if (form.id !== 'welcomeForm') requestAnimationFrame(() => success.focus({ preventScroll: false }));
      }
    });
  }

  function syncWelcomeViewport() {
    const height = window.visualViewport?.height || innerHeight;
    get('welcomeModal')?.style.setProperty('--welcome-viewport-height', `${height}px`);
  }

  function setupWelcome() {
    const modal = get('welcomeModal');
    syncWelcomeViewport();
    clearTimeout(welcomeTimer);
    if (welcomeScrollHandler) window.removeEventListener('scroll', welcomeScrollHandler);
    if (!modal || window.Shopify?.designMode) return;
    if (modal.querySelector('[data-newsletter-confirmed="true"],[data-newsletter-error="true"]')) { openSurface(modal); return; }
    const seen = Number(storageRead(welcomeStorage, 0));
    const repeat = Number(modal.dataset.welcomeRepeat || 30) * 86400000;
    if (seen && Date.now() - seen < repeat) return;
    const attempt = () => {
      if (welcomeOpened) return;
      if (activeSurface || document.hidden) { welcomeTimer = setTimeout(attempt, 3000); return; }
      openSurface(modal);
    };
    welcomeTimer = setTimeout(attempt, Number(modal.dataset.welcomeDelay || 6000));
    welcomeScrollHandler = () => {
      const distance = Math.max(1, document.documentElement.scrollHeight - innerHeight);
      if (scrollY / distance < 0.35) return;
      window.removeEventListener('scroll', welcomeScrollHandler);
      attempt();
    };
    window.addEventListener('scroll', welcomeScrollHandler, { passive: true });
  }

  function setupMotion(root = document) {
    if (!revealObserver && 'IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }), { threshold: 0.08, rootMargin: '0px 0px 40px 0px' });
    }
    root.querySelectorAll('.reveal:not([data-o2o-observed])').forEach(node => {
      node.dataset.o2oObserved = 'true';
      if (revealObserver) revealObserver.observe(node); else node.classList.add('is-visible');
    });
    if (!videoObserver && 'IntersectionObserver' in window) {
      videoObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting && !document.hidden) entry.target.play().catch(() => {});
        else entry.target.pause();
      }), { threshold: 0.1, rootMargin: '100px' });
    }
    root.querySelectorAll('video[data-autoplay-video]:not([data-o2o-observed])').forEach(video => {
      video.dataset.o2oObserved = 'true';
      video.muted = true;
      if (videoObserver) videoObserver.observe(video);
      else video.play().catch(() => {});
    });
  }

  function syncFooterMenus() {
    document.querySelectorAll('.footer-accordion-trigger').forEach(trigger => {
      const expanded = !mobile.matches || trigger.dataset.mobileExpanded === 'true';
      trigger.setAttribute('aria-expanded', String(expanded));
      const panel = get(trigger.getAttribute('aria-controls'));
      if (panel) panel.hidden = !expanded;
    });
  }

  function init(root = document) {
    document.querySelector('[data-o2o-shell]')?.classList.add('o2o-shell-ready');
    document.querySelectorAll('[data-mobile-nav]').forEach(control => {
      if (!control.hasAttribute('data-mobile-base-active')) control.dataset.mobileBaseActive = String(control.classList.contains('is-active'));
    });
    root.querySelectorAll('.has-mega:not([data-o2o-bound])').forEach(item => {
      item.dataset.o2oBound = 'true';
      item.addEventListener('mouseenter', () => { if (!mobile.matches) openMega(item); });
      item.addEventListener('mouseleave', () => {
        timers.set(item, setTimeout(() => { if (!item.contains(document.activeElement)) closeMegaMenus(); }, 260));
      });
      item.addEventListener('focusin', () => { if (!mobile.matches) openMega(item); });
      item.addEventListener('focusout', event => { if (!item.contains(event.relatedTarget)) closeMegaMenus(); });
    });
    syncFooterMenus();
    setupMotion(root);
    syncHeader();
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    const opener = target.closest('[data-o2o-surface-open],[data-cart-open]');
    if (opener) {
      const id = opener.dataset.o2oSurfaceOpen || 'cartDrawer';
      if (get(id)) { event.preventDefault(); if (activeSurface?.id === id) closeSurface(id); else openSurface(id); }
      return;
    }
    const closer = target.closest('[data-o2o-surface-close]');
    if (closer) { event.preventDefault(); closeSurface(closer.dataset.o2oSurfaceClose); return; }
    if (target === get('siteBackdrop') || target === activeSurface && activeSurface.matches('.modal')) { closeAll(); return; }
    const megaToggle = target.closest('.mega-keyboard-toggle');
    if (megaToggle) {
      const item = megaToggle.closest('.has-mega');
      if (item.classList.contains('is-open')) closeMegaMenus(); else openMega(item);
      return;
    }
    const accordion = target.closest('[data-mobile-accordion],.footer-accordion-trigger');
    if (accordion) {
      if (accordion.matches('.footer-accordion-trigger') && !mobile.matches) return;
      const isOpen = accordion.getAttribute('aria-expanded') === 'true';
      const scope = accordion.closest('.mobile-nav-links,.footer-grid');
      scope?.querySelectorAll('[aria-controls]').forEach(control => {
        control.setAttribute('aria-expanded', 'false');
        if (control.matches('.footer-accordion-trigger')) control.dataset.mobileExpanded = 'false';
        const panel = get(control.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
      if (!isOpen) { accordion.setAttribute('aria-expanded', 'true'); const panel = get(accordion.getAttribute('aria-controls')); if (panel) panel.hidden = false; }
      if (accordion.matches('.footer-accordion-trigger')) accordion.dataset.mobileExpanded = String(!isOpen);
      return;
    }
    const recent = target.closest('[data-recent-search]');
    if (recent) { get('searchInput').value = recent.dataset.recentSearch; searchProducts(recent.dataset.recentSearch); get('searchInput').focus(); return; }
    if (target.closest('#searchProducts a,#searchViewAll')) rememberSearch(get('searchInput')?.value || '');
    if (activeSurface?.id === 'accountPopover' && !target.closest('#accountPopover')) closeSurface(activeSurface, { restoreFocus: false });
    if (!target.closest('.has-mega')) closeMegaMenus();
  });
  document.addEventListener('input', event => {
    if (event.target.id !== 'searchInput') return;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => searchProducts(event.target.value.trim()), 180);
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-localization-select]')) event.target.form.requestSubmit();
  });
  document.addEventListener('submit', event => {
    if (event.target.matches('.search-field')) rememberSearch(get('searchInput')?.value || '');
    if (event.target.matches('[data-newsletter-form]')) {
      guardNewsletterSubmit(event);
    }
  }, true);
  document.addEventListener('keydown', event => {
    trapFocus(event);
    if (event.key !== 'Escape') return;
    if (activeSurface) { event.preventDefault(); closeAll(); }
    else {
      const openMenu = document.querySelector('.has-mega.is-open');
      openMenu?.querySelector('.nav-link')?.focus();
      closeMegaMenus();
    }
  });
  window.addEventListener('scroll', syncHeader, { passive: true });
  window.addEventListener('resize', syncWelcomeViewport, { passive: true });
  window.visualViewport?.addEventListener('resize', syncWelcomeViewport, { passive: true });
  window.addEventListener('popstate', () => {
    historyBackPending = false;
    if (historyQueuedSurface) {
      const queued = historyQueuedSurface;
      historyQueuedSurface = null;
      if (activeSurface?.id === queued.id) markSurfaceHistory(queued.id);
      else openSurface(queued.id);
      return;
    }
    const entry = ownedHistorySurface();
    if (entry && get(entry.id)) openSurface(entry.id, { history: false });
    else closeAll({ history: false });
  });
  mobile.addEventListener('change', () => {
    if (!mobile.matches && activeSurface?.id === 'mobileNav') closeAll();
    if (mobile.matches && document.body.dataset.page === 'collection' && activeSurface?.matches('.collection-curtain')) closeAll({ restoreFocus: false });
    syncFooterMenus();
    renderRecentSearches();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('video[data-autoplay-video]').forEach(video => video.pause());
    else document.querySelectorAll('video[data-autoplay-video]').forEach(video => {
      const rect = video.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < innerHeight) video.play().catch(() => {});
    });
  });
  document.addEventListener('shopify:section:load', event => { originalSearch = null; init(event.target); setupNewsletterForms(); setupWelcome(); });
  document.addEventListener('shopify:section:unload', event => { if (event.target.contains(activeSurface)) closeAll({ restoreFocus: false }); });
  document.addEventListener('o2o:content-updated', event => setupMotion(event.detail?.root || document));
  window.O2OShell = { open: openSurface, close: closeSurface, closeAll, init, reveal: setupMotion };
  init();
  setupDebugBorderGuard();
  setupNewsletterForms();
  setupWelcome();
})();
